import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {WidthItem} from 'src/components/width-item';

import {FlatButton} from '@pb/kit-component-button';
import {Group} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';
import {ProgressBar} from '@pb/kit-component-stepper';
import {colors} from '@pb/kit-stylus/config.json';


import type {
    BulkActionIdSelector,
    BulkActionTaskSelector,
    GetProgress,
    SetResultShown,
    StopAction,
} from './typedef';

type Props = {
    label: string,

    taskSelector: BulkActionTaskSelector,
    idSelector: BulkActionIdSelector,

    getProgress: GetProgress,
    stopAction?: StopAction,
    setShown: SetResultShown,

    onDone?: (success: boolean) => void,
};

export const BulkActionProgress = ({
    label,
    taskSelector,
    idSelector,
    getProgress,
    stopAction,
    setShown,
    onDone,
}: Props) => {
    const dispatch = useDispatch();

    const backgroundTask = useSelector(taskSelector);
    const uuid = useSelector(idSelector);

    const [progress, setProgress] = useState(0);
    const [isStopped, setStopped] = useState(false);
    const mounted = useRef(false);
    const pollingStarted = useRef(false);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    useEffect(() => {
        if (!uuid || !backgroundTask || pollingStarted.current) {
            return;
        }
        pollingStarted.current = true;

        const fail = () => {
            backgroundTask.completeFailure('An error occurred while processing items.');
            onDone?.(false);
        };

        const pollAndUpdateTaskStatus = async () => {
            try {
                const progressAction = await getProgress(uuid);
                if (progressAction.payload.success) {
                    const bulkProgress = progressAction.payload.data;
                    const processed = bulkProgress.completed + bulkProgress.canceled + bulkProgress.failed;
                    if (mounted.current) {
                        setProgress(bulkProgress.total ? 100 * processed / bulkProgress.total : 100);
                    }
                    if (bulkProgress.total === processed) {
                        setTimeout(() => {
                            dispatch(setShown(true));
                            onDone?.(true);
                            if (bulkProgress.failed) {
                                backgroundTask.completeFailure();
                            } else {
                                backgroundTask.completeSuccess();
                            }
                        }, 1000);
                    } else {
                        setTimeout(pollAndUpdateTaskStatus, 2000);
                    }
                } else {
                    fail();
                }
            } catch {
                fail();
            }
        };

        // Dismissing a notification does not cancel the job or its completion callbacks.
        setTimeout(pollAndUpdateTaskStatus, 2000);
    }, [uuid, backgroundTask, getProgress, dispatch, setShown, onDone]);

    const stopBulkAction = useCallback(() => {
        setStopped(true);
        if (stopAction) {
            stopAction(uuid);
        }
    }, [stopAction, uuid]);

    return (
        <div>
            <Label>{label}</Label>
            <Group alignItems="center">
                <WidthItem width={300}>
                    <ProgressBar
                        progress={progress || 0}
                        fullColor={colors.primaryColors.skyBlueDarkest}
                        emptyColor={colors.primaryColors.gray}
                    />
                </WidthItem>
                <span>{Math.round(progress || 0)} %</span>
                {stopAction ? (
                    <FlatButton onClick={stopBulkAction} disabled={isStopped}>
                        <b>Cancel</b>
                    </FlatButton>
                ) : null}
            </Group>
        </div>
    );
};
